import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@lf/ui-primitives';
import {
    getCoppaConsentStatus,
    resendCoppaConsentEmail,
    changeCoppaConsentEmail,
} from '../../api/lfApiChildLink';

function CoppaConsentPendingCard({ initialEmail = '', fetchUser }) {
    const [status, setStatus] = useState({
        coppa_required: true,
        status: 'pending',
        parent_email: initialEmail,
    });
    const [editingEmail, setEditingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    async function refresh() {
        const resp = await getCoppaConsentStatus();
        if (resp && resp.data) {
            setStatus(resp.data);
            if (resp.data.status === 'granted' && fetchUser) {
                fetchUser();
            }
        }
    }

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 5000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleResend() {
        setError('');
        setMessage('');
        setBusy(true);
        const resp = await resendCoppaConsentEmail();
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            setMessage('Email resent. Any previous link is no longer valid.');
        } else {
            setError('Could not resend the email. Please try again.');
        }
    }

    async function handleChangeEmail(e) {
        if (e) e.preventDefault();
        setError('');
        setMessage('');
        const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
        if (!emailRegex.test(newEmail)) {
            setError('Please enter a valid email address.');
            return;
        }
        setBusy(true);
        const resp = await changeCoppaConsentEmail(newEmail);
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            setMessage('Email updated. The previous link is no longer valid.');
            setEditingEmail(false);
            setNewEmail('');
            refresh();
        } else {
            setError('Could not update the email. Please try again.');
        }
    }

    return (
        <div className="mx-auto max-w-screen-md my-8">
            <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 w-full text-left">
                <h2 className="text-xl font-semibold mb-3">
                    Waiting for your grown-up
                </h2>
                <p className="text-sm text-gray-700 mb-2">
                    We sent an email to <strong>{status.parent_email || '...'}</strong>
                    {' '}with a link they can click to say it&apos;s OK for you
                    to use Learnforest.
                </p>
                <p className="text-sm text-gray-700 mb-4">
                    As soon as they click the link, your account will turn on.
                    You can leave this page open — it will update on its own.
                </p>

                {message && <div className="text-green-600 text-sm mb-2">{message}</div>}
                {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

                <div className="flex flex-col gap-2 w-full max-w-xs">
                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        text="Send the email again"
                        isLoading={busy}
                        onClick={handleResend}
                    />
                    {!editingEmail ? (
                        <button
                            className="text-blue-500 underline text-sm text-left mt-1"
                            onClick={() => setEditingEmail(true)}
                            type="button"
                        >
                            Wrong email? Type a new one
                        </button>
                    ) : (
                        <form onSubmit={handleChangeEmail} className="flex flex-col gap-2 mt-2">
                            <input
                                type="email"
                                className="border border-gray-300 p-1 w-full"
                                placeholder="New parent email"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                            />
                            <div className="flex gap-2">
                                <LoadingButton
                                    type="submit"
                                    className="bg-blue-500 text-white py-1 px-3 rounded text-sm"
                                    text="Use this email"
                                    isLoading={busy}
                                    onClick={handleChangeEmail}
                                />
                                <button
                                    type="button"
                                    className="text-gray-600 underline text-sm"
                                    onClick={() => { setEditingEmail(false); setError(''); }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                <p className="text-xs text-gray-500 mt-6">
                    Sending the email again or changing the email turns off
                    the old link, so only the newest one will work.
                </p>
            </div>
        </div>
    );
}

export default CoppaConsentPendingCard;
